import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import { reportWebVitals } from "./lib/vitals";
import "./styles/global.css";

// Started before the app so the observers are watching the first frame, not the
// frame after React finished with it. Development is skipped: Vite injects the
// stylesheet from JavaScript there, which is a layout shift the deployed site does
// not have, and reporting it would only teach us about the dev server.
if (import.meta.env.PROD) reportWebVitals();

const root = document.getElementById("root");
if (!root) throw new Error("Application root is missing.");
const container = root;

function mount(): void {
  createRoot(container).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}

// The page the server sent is already on screen with its real content. Mounting
// immediately threw that away and showed a loading screen, then the board a moment
// later: two visible jumps for a request that boot.js started before this bundle had
// even downloaded and that is normally already resolved by now. So wait for it and
// mount once, with the puzzle in hand.
//
// The cap is what keeps a slow or failed request from holding the page hostage — past
// it the app mounts and shows its own loading and error states, exactly as before.
const FIRST_RENDER_GRACE_MS = 600;
const prefetched = window.__nonogramPrefetch?.promise;

if (prefetched) {
  let mounted = false;
  const mountOnce = (): void => {
    if (mounted) return;
    mounted = true;
    mount();
  };
  void prefetched.then(mountOnce, mountOnce);
  window.setTimeout(mountOnce, FIRST_RENDER_GRACE_MS);
} else {
  mount();
}

if ("serviceWorker" in navigator && import.meta.env.PROD) {
  window.addEventListener("load", () => {
    void navigator.serviceWorker.register("/service-worker.js");
  });
}
